import { isDefinedArray, isPositiveNumber } from "et3-type-guards";
import { memoize, sortBy } from "lodash";
import { computed, observable, runInAction } from "mobx";

import { apiCallGetApprovalData } from "../../api/approve-reject-api/api-call-get-approval-data";
import { TDescriptionType, TCorePage, TUserEntry } from "../../Types";
import getCleanPoolStore from "../core/core-clean-pool";
import getCorePopUpStore from "../core/core-popup-store";
import getCoreUIStore from "../core/core-ui-store";
import getDashBoardStore from "../dashboard-store";
import {
  E_SESSION_STORAGE_KEY,
  E_STORAGE_TYPE
} from "../../constants/storage-constants";
import getCoreStorageStore from "../core/core-storage-store";
import approveUserDescriptionOnDatabase from "./approve-user-description";
import approveUserPictureOnDatabase from "./approve-user-image";
import rejectUserDescriptionOnDatabase from "./reject-user-description";
import rejectUserPictureOnDatabase from "./reject-user-image";
import { TEt3PickerSingleValue } from "../../TypesNEW";
import { reducerToZeroIfUndefined } from "../../helpers/et3-picker-data-helper";

class ApproveRejectStore {
  selectedLimit = observable.box<TEt3PickerSingleValue>({
    label: "10",
    value: "10"
  });
  approvalData = observable.box<TUserEntry[]>([]);
  actionStatus = observable.map<TDescriptionType, Map<number, boolean>>();
  createdFromType = observable.box<TEt3PickerSingleValue>({
    label: "Other",
    value: "other"
  });

  setActionStatus = (
    type: TDescriptionType,
    id: number,
    isPressed: boolean
  ) => {
    const typeMap = this.actionStatus.get(type) || new Map<number, boolean>();
    typeMap.set(id, isPressed);

    runInAction(() => {
      this.actionStatus.set(type, typeMap);
    });
  };

  getActionStatus = (type: TDescriptionType, id: number): boolean => {
    const typeMap = this.actionStatus.get(type);
    return typeMap ? typeMap.get(id) : false;
  };

  setApprovalData = (data: TUserEntry[]) => {
    runInAction(() => {
      this.approvalData.set(data);
    });
  };

  setSelectedLimit = (limit: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.selectedLimit.set(limit);
    });
  };

  setApproveRejectCreatedFromType = (status: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.createdFromType.set(status);
    });
  };

  approveRejectCounter = computed(() => {
    const approveDataCount = this.approvalData.get().reduce((count, item) => {
      let counter = count;

      if (item.descriptionStatus === "pending") {
        counter++;
      }
      if (item.peerDescriptionStatus === "pending") {
        counter++;
      }
      if (
        item.albumPicture.some(
          (pic) => pic.status === "P" && pic.isActive === 1
        )
      ) {
        counter++;
      }

      return counter;
    }, 0);

    return approveDataCount;
  });

  getAwaitingApprovalData = async () => {
    const definedLimit = reducerToZeroIfUndefined(
      parseInt(this.selectedLimit.get().value, 10)
    );
    const createdFromType = this.createdFromType.get().value;
    if (!isPositiveNumber(definedLimit)) {
      return;
    }
    getCoreUIStore().setIsLoading("APPROVAL_DATA", true);
    const { status, code, data } = await apiCallGetApprovalData({
      createdFromType,
      limit: definedLimit
    });
    if (status === "OK" && isDefinedArray(data)) {
      this.actionStatus.clear();
      this.setApprovalData(data);
    } else if (status === "ERROR") {
      this.setApprovalData([]);
      getCorePopUpStore().showAlertMessage(code);
    }
    getCoreUIStore().setIsLoading("APPROVAL_DATA", false);
  };

  cleanPool = async () => {
    await getCleanPoolStore().cleanPool({
      currentData: this.approvalData,
      page: "APPROVAL_DATA",
      poolType: "APPROVAL",
      successVoidFunction: () => this.setApprovalData([])
    });
  };

  approveUserPicture = async (
    userId: number,
    userUUID: string,
    from: TCorePage,
    imageID: string,
    resolvingPendindData: boolean,
    buttonID: string
  ) => {
    getCoreUIStore().setButtonInAction(buttonID, true);

    await approveUserPictureOnDatabase(
      userId,
      userUUID,
      from,
      imageID,
      resolvingPendindData
    );
    getCoreUIStore().setButtonInAction(buttonID, false);
  };

  rejectUserPicture = async (
    userId: number,
    userUUID: string,
    from: TCorePage,
    imageID: string,
    resolvingPendindData: boolean,
    buttonID: string
  ) => {
    getCoreUIStore().setButtonInAction(buttonID, true);
    await rejectUserPictureOnDatabase(
      userId,
      userUUID,
      from,
      imageID,
      resolvingPendindData
    );

    getCoreUIStore().setButtonInAction(buttonID, false);
  };

  decreaseDashboardApprovalCount = () => {
    const approvalCount = getDashBoardStore().approvalCount.get();
    if (approvalCount > 0) {
      getDashBoardStore().approvalCount.set(approvalCount - 1);
      getCoreStorageStore(E_STORAGE_TYPE.SESSION).set(
        E_SESSION_STORAGE_KEY.COUNTS,
        {
          reportsCount: getDashBoardStore().reportsCount.get(),
          reportedCount: getDashBoardStore().reportedCount.get(),
          approvalCount: getDashBoardStore().approvalCount.get(),
          investigationCasesCount:
            getDashBoardStore().investigationCasesCount.get(),
          activeTime: getDashBoardStore().activeTime.get()
        }
      );
    }
  };
  approveUserDescription = async (
    descriptionID: number,
    userId: number,
    userUUID: string,
    from: TCorePage,
    descriptionType: TDescriptionType,
    resolvingPendindData: boolean,
    buttonID: string
  ) => {
    getCoreUIStore().setButtonInAction(buttonID, true);
    await approveUserDescriptionOnDatabase(
      descriptionID,
      userId,
      userUUID,
      from,
      descriptionType,
      resolvingPendindData
    );
    getCoreUIStore().setButtonInAction(buttonID, false);
  };

  rejectUserDescription = async (
    descriptionID: number,
    userId: number,
    userUUID: string,
    from: TCorePage,
    descriptionType: TDescriptionType,
    resolvingPendindData: boolean,
    buttonID: string
  ) => {
    getCoreUIStore().setButtonInAction(buttonID, true);
    await rejectUserDescriptionOnDatabase(
      descriptionID,
      userId,
      userUUID,
      from,
      descriptionType,
      resolvingPendindData
    );
    getCoreUIStore().setButtonInAction(buttonID, false);
  };

  orderedAlbumPictureArray = (
    albumPicture: [
      {
        id: number;
        picture: string;
        status: string;
        isActive: 0 | 1;
      }
    ]
  ) => {
    return sortBy(albumPicture, [
      (item) => {
        if (item.status === "P" && item.isActive === 1) return 0;
        if (item.status !== "P") return 1;
        return 2;
      }
    ]);
  };
}

const getApproveRejectStore = memoize(
  () => new ApproveRejectStore(),
  () => 1
);
export default getApproveRejectStore;
